import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MEAL_SLOTS } from '@kraftwerk/shared';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { createFromImport } from '../../db/nutrition.js';
import { uploadRecipeImage } from '../../db/recipeImages.js';
import { fetchImport, imageBlob } from '../../db/recipeImport.js';
import type { ImportPreview, ImportProblem } from '../../db/recipeImport.js';

/**
 * Rezept von einer Webseite: Link einfügen → Vorschau → übernehmen.
 *
 * Übernommen werden nur die maschinenlesbaren Rezeptdaten der Seite. Zutaten
 * kommen als freie Zeilen ohne Nährwerte — zugeordnet wird danach in der
 * Bearbeiten-Ansicht. Das sagt die Vorschau vorher, nicht erst hinterher.
 */
export function RecipeImport({
  onDone,
  onCancel,
}: {
  onDone: (recipeId: string) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const urlId = useId();
  const slotId = useId();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ImportProblem | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [slot, setSlot] = useState<MealSlot>('dinner');
  const imageUrl = usePreviewImage(preview);

  if (preview) {
    const { recipe } = preview;
    return (
      <section aria-labelledby="import-preview" className="kw-tile flex flex-col gap-3">
        <h3 id="import-preview" className="font-display text-xl font-bold break-words">
          {recipe.name}
        </h3>
        {imageUrl ? (
          <img src={imageUrl} alt="" className="aspect-[16/9] w-full rounded-xl object-cover" />
        ) : null}
        <p className="text-sm">
          {t('import.summary', {
            ingredients: recipe.ingredients.length,
            steps: recipe.steps.length,
          })}
          {recipe.portions ? ` · ${t('dish.portions', { count: recipe.portions })}` : ''}
        </p>
        {recipe.nutrition ? (
          <p className="text-xs text-ink-muted">
            {t('import.nutrition', {
              kcal: Math.round(recipe.nutrition.kcal),
              protein: recipe.nutrition.protein.toLocaleString(i18n.language),
            })}
          </p>
        ) : (
          <p className="text-xs text-ink-muted">{t('import.noNutrition')}</p>
        )}
        <label htmlFor={slotId} className="flex flex-col gap-1">
          <span className="kw-label">{t('dish.meal')}</span>
          <select
            id={slotId}
            value={slot}
            onChange={(event) => setSlot(event.target.value as MealSlot)}
            className="kw-field"
          >
            {MEAL_SLOTS.map((option) => (
              <option key={option} value={option}>
                {t(`nutrition.slot.${option}`)}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void createFromImport(
              {
                name: recipe.name,
                slot,
                portions: recipe.portions,
                ingredients: recipe.ingredients,
                steps: recipe.steps,
                sourceUrl: preview.sourceUrl,
                nutrition: recipe.nutrition ?? null,
              },
              db,
            ).then(async (recipeId) => {
              // Das Bild hinterher: Scheitert es (offline), ist das Rezept trotzdem da.
              const blob = imageBlob(preview.image);
              if (blob) await uploadRecipeImage(recipeId, blob);
              onDone(recipeId);
            });
          }}
          className="kw-btn"
        >
          {busy ? t('import.saving') : t('import.take')}
        </button>
        <button type="button" onClick={onCancel} className="kw-btn kw-btn-quiet kw-btn-small">
          {t('nutrition.library.cancel')}
        </button>
      </section>
    );
  }

  return (
    <form
      className="kw-tile flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        setBusy(true);
        setProblem(null);
        void fetchImport(url).then((result) => {
          setBusy(false);
          if (result.ok) setPreview(result.preview);
          else setProblem(result.problem);
        });
      }}
    >
      <label htmlFor={urlId} className="flex flex-col gap-1">
        <span className="kw-label">{t('import.url')}</span>
        <input
          id={urlId}
          // Bewusst kein `type="url"`: Der Browser ließe „chefkoch.de/…" ohne
          // Schema nicht absenden — auf dem Telefon der Normalfall. Das Schema
          // ergänzt `withScheme`; die URL-Tastatur kommt über `inputMode`.
          type="text"
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://"
          className="kw-field"
          required
        />
      </label>
      <p className="text-xs text-ink-muted">{t('import.hint')}</p>
      {problem ? (
        <p role="alert" className="text-sm text-danger">
          {t(`import.problem.${problem}`)}
        </p>
      ) : null}
      <button type="submit" disabled={busy || url.trim() === ''} className="kw-btn">
        {busy ? t('import.busy') : t('import.fetch')}
      </button>
      <button type="button" onClick={onCancel} className="kw-btn kw-btn-quiet kw-btn-small">
        {t('nutrition.library.cancel')}
      </button>
    </form>
  );
}

/** Vorschau des Bildes als Objekt-Adresse — und wieder freigeben. */
function usePreviewImage(preview: ImportPreview | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const blob = imageBlob(preview?.image ?? null);
    if (!blob || typeof URL.createObjectURL !== 'function') return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [preview]);
  return url;
}
